/**
 * MODULE: domain/sales-target-management/frontend/utils/salesTargetPeriodUtils.ts
 * PURPOSE: Fiscal period labels and date boundaries for sales target forms
 */

import {
  fiscalPeriodFromDate,
  fiscalQuarterDateRange,
  type FiscalCalendarConfig,
  DEFAULT_FISCAL_CALENDAR,
} from '@shared/lib/frontend/utils/fiscalCalendar';
import type { PeriodType, SalesTarget } from '../../contracts/sales-targets.types';

export function currentFiscalQuarter(
  fiscalYearStartMonth = 1,
  referenceDate = new Date()
): number {
  return fiscalPeriodFromDate(referenceDate, { fiscalYearStartMonth }).fiscalQuarter;
}

export function currentFiscalYear(
  fiscalYearStartMonth = 1,
  referenceDate = new Date()
): number {
  return fiscalPeriodFromDate(referenceDate, { fiscalYearStartMonth }).fiscalYear;
}

export function calculateSalesTargetPeriodDates(
  periodType: PeriodType,
  year: number,
  quarter?: number,
  month?: number,
  fiscalCalendar: FiscalCalendarConfig = DEFAULT_FISCAL_CALENDAR
): { period_start_date: string; period_end_date: string } {
  if (periodType === 'quarterly' && quarter) {
    return fiscalQuarterDateRange(year, quarter, fiscalCalendar);
  }

  if (periodType === 'yearly') {
    const q1 = fiscalQuarterDateRange(year, 1, fiscalCalendar);
    const q4 = fiscalQuarterDateRange(year, 4, fiscalCalendar);
    return { period_start_date: q1.period_start_date, period_end_date: q4.period_end_date };
  }

  if (periodType === 'monthly' && month) {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    return {
      period_start_date: startDate.toISOString().split('T')[0],
      period_end_date: endDate.toISOString().split('T')[0],
    };
  }

  const today = new Date();
  return {
    period_start_date: today.toISOString().split('T')[0],
    period_end_date: today.toISOString().split('T')[0],
  };
}

export function formatSalesTargetPeriodLabel(target: SalesTarget): string {
  if (target.period_type === 'yearly') {
    return `FY ${target.fiscal_year}`;
  }
  if (target.period_type === 'quarterly' && target.fiscal_quarter) {
    return `Q${target.fiscal_quarter} ${target.fiscal_year}`;
  }
  if (target.period_type === 'monthly') {
    const date = new Date(target.period_start_date);
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  return target.period_type;
}
